import { z } from 'zod';
import operatorApiFetch from './apiService';
import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

// ── Shared inner schemas ────────────────────────────────────────────────

// Show endpoint embeds output_product / input_product with the form-driving
// flags (validity_days / manufacturing_date_required / batch_management_required)
// so ApplyBomButton can populate hidden fields the same way handleProductSelect
// does in ProductItemCard. Marked optional because get_records still emits
// only id/sku/name for the list row.
const ProductRefSchema = z.object({
  id: z.string(),
  sku: z.string(),
  name: z.string(),
  validity_days: z.number().nullish(),
  manufacturing_date_required: z.boolean().nullish(),
  batch_management_required: z.boolean().nullish(),
}).passthrough();

const InventoryTypeRefSchema = z.object({
  id: z.string(),
  name: z.string(),
}).passthrough();

// ── Per-endpoint schemas ────────────────────────────────────────────────

// Api::Operator::V1::ProductionBomsController#get_records (lines 90-104)
// Block emits: id, name, merchant_id, output_product:{id,sku,name},
//              output_inventory_type:{id,name}|null, production_bom_items_count, created_at
const ProductionBomListRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  output_product: ProductRefSchema,
  output_inventory_type: InventoryTypeRefSchema.nullable(),
  production_bom_items_count: z.number(),
  created_at: z.string(),
}).passthrough();

// Api::Operator::V1::ProductionBomsController#show (lines 14-37)
// Returns ProductionBom.as_json with nested include + editable/deletable flags.
// Rails `as_json(include: { x: ... })` OMITS the key when the association is
// nil (rather than emitting null), so optional associations use `.nullish()`
// (= optional + nullable) to accept both undefined and null.
const ProductionBomItemDetailSchema = z.object({
  id: z.string(),
  production_bom_id: z.string(),
  input_product_id: z.string(),
  input_inventory_type_id: z.string().nullable(),
  quantity: z.number(),
  input_product: ProductRefSchema,
  input_inventory_type: InventoryTypeRefSchema.nullish(),
}).passthrough();

const ProductionBomDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  output_product_id: z.string(),
  output_inventory_type_id: z.string().nullable(),
  output_product: ProductRefSchema,
  output_inventory_type: InventoryTypeRefSchema.nullish(),
  merchant: z.object({ id: z.string(), name: z.string() }).passthrough(),
  production_bom_items: z.array(ProductionBomItemDetailSchema),
  editable: z.boolean(),
  deletable: z.boolean(),
}).passthrough();

// get_ids returns just an array of strings
const ProductionBomIdsSchema = z.array(z.string());

// create / update return ProductionBom.as_json (without items/editable/deletable
// since the action doesn't pass those into render_data_item). Use the same shape
// minus those three to be safe.
const ProductionBomMutatedSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string(),
  output_product_id: z.string(),
  output_inventory_type_id: z.string().nullable(),
}).passthrough();

// destroy returns { id }
const ProductionBomDeletedSchema = z.object({
  id: z.string(),
}).passthrough();

// export returns { job_id }
const JobIdSchema = z.object({
  job_id: z.string(),
}).passthrough();

// ── Service ─────────────────────────────────────────────────────────────

export const productionBomService = {
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('production_boms/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(ProductionBomListRowSchema));
  },

  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('production_boms/get_ids', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiSuccess(ProductionBomIdsSchema));
  },

  get: async (id: string) => {
    const res = await operatorApiFetch(`production_boms/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(ProductionBomDetailSchema));
  },

  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('production_boms', {
      method: 'POST',
      body: { production_bom: data },
    });
    return parseApiResponse(res, apiSuccess(ProductionBomMutatedSchema));
  },

  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`production_boms/${id}`, {
      method: 'PUT',
      body: { production_bom: data },
    });
    return parseApiResponse(res, apiSuccess(ProductionBomMutatedSchema));
  },

  delete: async (id: string) => {
    const res = await operatorApiFetch(`production_boms/${id}`, {
      method: 'DELETE',
    });
    return parseApiResponse(res, apiSuccess(ProductionBomDeletedSchema));
  },

  export: async (ids: string[]) => {
    const res = await operatorApiFetch('production_boms/export', {
      method: 'POST',
      body: { ids },
    });
    return parseApiResponse(res, apiSuccess(JobIdSchema));
  },
};

export default productionBomService;

// z.infer types for callers that want strict typing
export type ProductionBomListRow = z.infer<typeof ProductionBomListRowSchema>;
export type ProductionBomDetail = z.infer<typeof ProductionBomDetailSchema>;
export type ProductionBomItemDetail = z.infer<typeof ProductionBomItemDetailSchema>;
